import React from 'react';
import Slider from 'react-slick';
import {
  StyledHeroContainer,
  StyledHeroSection,
  StyledHeaderSection,
  StyledGrid,
  StyledTextContainer,
  StyledImage,
  StyledHeading,
  StyledSubeading,
  StyledDisclaimer,
  StyledButtonWrapper,
  StyledImageContainer
} from './HeroV1.styled';

import Pill from '@/components/Pill/Pill';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';

const COMPONENT_ID = 'hero-section-new-v1';

/**
 * HeroV1 component displays a hero section with an image, text content, and optional pill and button.
 *
 * @param {Object} props - The component props
 * @param {string} [props.pillColor] - Background color of the pill component
 * @param {React.ReactNode} [props.pillIcon] - Icon to display in the pill component
 * @param {string} [props.pillText] - Text to display in the pill component
 * @param {string} [props.pillTextColor] - Text color of the pill component
 * @param {string} props.heading - Main heading text of the hero section
 * @param {string} props.subHeading - Sub-heading text displayed below the main heading
 * @param {string} props.imageUrl - URL of the hero image to display
 * @param {React.ReactNode} [props.buttonComponent] - Optional button component to display below the text
 * @param {string} [props.trackingId] - Optional tracking ID for analytics purposes
 * @param {Object} [rest] - Additional props to be spread to the root element
 * @returns {React.ReactElement} A hero section component with image and text content
 */
const HeroV1 = props => {
  const SLIDER_CONFIG = {
    dots: true,
    infinite: true,
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    pauseOnHover: true
  };
  const componentIdentifier = props.trackingId
    ? `${props.trackingId}-${COMPONENT_ID}`
    : `${COMPONENT_ID}`;
  const settings = {
    ...SLIDER_CONFIG,
    autoplaySpeed: props.timer ? props.timer : 5000
  };
  return (
    <StyledHeroContainer
      className={`${componentIdentifier}`}
      {...props.rest}
      marginTop={props.marginTop}
      marginTopMobile={props.marginTopMobile}
      background={props.background}
    >
    <StyledHeroSection
      className={`${componentIdentifier}-hero-section`}
      slides={props.slides}
    >
      <Slider {...settings}>
        {props.slides?.map((slide, index) => {
          return (
            <StyledHeaderSection
              key={index}
              className={`${componentIdentifier}-header`}
            >
              <StyledGrid
                isMobileReversed={slide.isMobileReversed}
                className={`${componentIdentifier}-grid`}
              >
                <StyledTextContainer
                  className="page-text-container"
                  slides={props.slides}
                  isMobileReversed={slide.isMobileReversed}
                >
                  {slide.pillText && (
                    <Pill
                      className={`${componentIdentifier}-pill`}
                      pillColor={slide.pillColor}
                      pillText={slide.pillText}
                      pillTextColor={slide.pillTextColor}
                      pillIcon={slide.pillIcon}
                      pillPadding={slide.pillPadding}
                    />
                  )}
                  <StyledHeading
                    className={`${componentIdentifier}-heading-wrapper`}
                  >
                    <Typography.SH1 semibold color={REBRAND_COLOR.WHITE}>
                      {slide.heading}
                    </Typography.SH1>
                  </StyledHeading>
                  <StyledSubeading
                    className={`${componentIdentifier}-subheading-wrapper`}
                  >
                    <Typography.SH4 color={REBRAND_COLOR.WHITE}>
                      {slide.subHeading}
                    </Typography.SH4>
                  </StyledSubeading>
                  {slide.buttonComponent && (
                    <StyledButtonWrapper
                      className={`${componentIdentifier}-button-wrapper`}
                    >
                      {slide.buttonComponent}
                    </StyledButtonWrapper>
                  )}
                  {slide.tcText && (
                    <StyledDisclaimer
                      className={`${componentIdentifier}-disclaimer-wrapper`}
                    >
                      <Typography.Body small color={REBRAND_COLOR.WHITE}>
                        {slide.tcText}
                      </Typography.Body>
                    </StyledDisclaimer>
                  )}
                </StyledTextContainer>
                <StyledImageContainer
                  slides={props.slides}
                  isMobileReversed={slide.isMobileReversed}
                >
                  <StyledImage
                    className={`${componentIdentifier}-image`}
                    src={slide.imageUrl}
                    alt={slide.heading}
                  />
                </StyledImageContainer>
              </StyledGrid>
            </StyledHeaderSection>
          );
        })}
      </Slider>
    </StyledHeroSection>
    </StyledHeroContainer>
  );
};

export default HeroV1;
